import type { ReleaseNote, ReleaseNoteEntry } from './types';

/**
 * Transforms a Contentful release note entry into a simplified ReleaseNote object
 *
 * @param entry - The Contentful entry to transform
 * @returns Transformed release note object
 * @throws Error if required fields are missing
 */
export function transformReleaseNoteEntry(
  entry: ReleaseNoteEntry
): ReleaseNote {
  // Validate required fields
  if (!entry.fields.title) {
    throw new Error(
      `Release note ${entry.sys.id} is missing required field: title`
    );
  }
  if (!entry.fields.date) {
    throw new Error(
      `Release note ${entry.sys.id} is missing required field: date`
    );
  }
  if (!entry.fields.content) {
    throw new Error(
      `Release note ${entry.sys.id} is missing required field: content`
    );
  }
  if (!entry.fields.author) {
    throw new Error(
      `Release note ${entry.sys.id} is missing required field: author`
    );
  }
  if (!entry.fields.authorImage) {
    throw new Error(
      `Release note ${entry.sys.id} is missing required field: authorImage`
    );
  }

  const authorImage = entry.fields.authorImage as any;
  const imageUrl = authorImage.fields?.file?.url as string | undefined;

  if (!imageUrl) {
    throw new Error(`Release note ${entry.sys.id} has invalid author image`);
  }

  return {
    id: entry.sys.id,
    title: entry.fields.title,
    date: new Date(entry.fields.date),
    content: entry.fields.content,
    categories: entry.fields.categories || [],
    author: entry.fields.author,
    authorImage: {
      url: imageUrl?.startsWith('//') ? `https:${imageUrl}` : imageUrl || '',
      title: (authorImage.fields?.title as string | undefined) || '',
      description: authorImage.fields?.description as string | undefined,
    },
  };
}
